import React, { useContext, useState } from "react";
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import _ from "lodash";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import Spinner from "../../../components/shared/Spinner";
import { NoDataBanner } from "../../../components/shared/NoDataBanner";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { useTranslation } from "../../../utils/useTranslation";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getOrderByDocNum } from "../../../services/deliveryService";
import { headers } from "../utils/deliveryHeaders";
import { nestedHeaders } from "../utils/deliveryDetailsHeaders";
import { deliveryModel } from "../../../models/reportsModel";
import { useAppTheme } from "../../../hooks/useAppTheme";

interface OrderNumberSearchModalProps {
  visible: boolean;
  onClose: () => void;
  onDownloadPdf: (row: any) => void;
  onRowClick: (row: any) => void;
  setSelected: (row: any) => void;
  setDeliveryModalVisible: (visible: boolean) => void;
  setDeliveryStatusModal: (visible: boolean) => void;
}

// A single order number can come back as more than one document (Sales Order
// row-group + Delivery Note row-group) — group by DocNum+DocType so each
// document keeps its own item lines instead of merging together.
const groupOrderResults = (rows: Array<deliveryModel>) => {
  const grouped = _.groupBy(rows, (row: any) => `${row.DocNum}-${row.DocType}`);
  return Object.values(grouped).map((items) => ({
    ...items[0],
    items,
  }));
};

export const OrderNumberSearchModal: React.FC<OrderNumberSearchModalProps> = ({
  visible,
  onClose,
  onDownloadPdf,
  onRowClick,
  setSelected,
  setDeliveryModalVisible,
  setDeliveryStatusModal,
}) => {
  const { t } = useTranslation();
  const { colors, isDark } = useAppTheme();
  const styles = React.useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { isSmallDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );
  const [searchTerms, setSearchTerms] = useState<Array<string>>([]);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<Array<any>>([]);

  const getModalWidth = () => {
    if (isSmallDevice) return "95%";
    if (isMediumDevice) return "90%";
    return "80%";
  };

  const handleClose = () => {
    setSearchTerms([]);
    setSearchInput("");
    setError("");
    setSearched(false);
    setResults([]);
    onClose();
  };

  const onSearchInputChange = (text: string) => {
    if (text.includes(",")) {
      const parts = text.split(",");
      const newChips = parts
        .slice(0, -1)
        .map((part) => part.trim())
        .filter(Boolean);
      if (newChips.length > 0) setSearchTerms((prev) => [...prev, ...newChips]);
      setSearchInput(parts[parts.length - 1]);
    } else {
      setSearchInput(text);
    }
  };

  const removeSearchTerm = (index: number) => {
    setSearchTerms((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSearch = () => {
    const docNums = [...searchTerms, searchInput.trim()].filter(Boolean);
    const isValid = docNums.length > 0 && docNums.every((value) => /^\d+$/.test(value));
    if (!isValid) {
      setError(
        t("orderNumberMustBeNumber") ||
          "Order number must be a number, or a comma-separated list of numbers",
      );
      return;
    }
    setError("");
    setLoading(true);
    setSearched(false);
    getOrderByDocNum(docNums.join(","))
      .then((result: any) => {
        setResults(groupOrderResults(result.data.recordset || []));
        setSearched(true);
      })
      .catch((err: any) => {
        setError(
          typeof err === "string" ? err : "Order not found. Please try again.",
        );
        setResults([]);
        setSearched(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { width: getModalWidth() }]}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {t("searchArchivedOrders")}
            </Text>
            <TouchableOpacity onPress={handleClose}>
              <MaterialCommunityIcons name="close" size={22} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.inputRow}>
            <View style={styles.inputWrapper}>
              <MaterialCommunityIcons
                name="magnify"
                size={18}
                color={colors.textMuted}
                style={styles.searchIcon}
              />
              {searchTerms.map((term, index) => (
                <View key={`${term}-${index}`} style={styles.searchChip}>
                  <Text style={styles.searchChipText}>{term}</Text>
                  <TouchableOpacity
                    style={styles.searchChipRemove}
                    onPress={() => removeSearchTerm(index)}
                  >
                    <Text style={styles.searchChipX}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
              <TextInput
                value={searchInput}
                onChangeText={onSearchInputChange}
                onSubmitEditing={handleSearch}
                style={styles.input}
                placeholder={
                  searchTerms.length === 0
                    ? t("orderNumberPlaceholder")
                    : "Add another..."
                }
                placeholderTextColor={colors.textMuted}
                keyboardType="default"
              />
            </View>
            <TouchableOpacity style={styles.searchButton} onPress={handleSearch}>
              <MaterialCommunityIcons
                name="magnify"
                size={16}
                color="#fff"
                style={{ marginRight: 4 }}
              />
              <Text style={styles.searchText}>{t("search") || "Search"}</Text>
            </TouchableOpacity>
          </View>
          {error ? <Text style={styles.errorText}>{error}</Text> : null}

          <View style={styles.resultsContainer}>
            {loading ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "10%" }}
              />
            ) : searched && results.length === 0 && !error ? (
              <NoDataBanner />
            ) : results.length > 0 ? (
              <DataTableWithPagination
                headers={headers}
                rows={results}
                itemsPerPage={20}
                useModal={true}
                isRowsExpandable={true}
                nestedHeader={nestedHeaders}
                rowKeyValue="DocNum"
                nestedItemKeyValue="items"
                isDetail={true}
                isSubDetail={true}
                isEdit={false}
                isDelete={false}
                isDeliveryAttachment={true}
                showAttachmentIcon={false}
                showDeliveryStatusIcon={false}
                hideInfoIcon={false}
                columnsToNotHaveThousandSeparator={["documentNo"]}
                setModalVisible={setDeliveryModalVisible}
                setDeliveryModalVisible={setDeliveryModalVisible}
                setDeliveryStatusModal={setDeliveryStatusModal}
                handleRowClick={onRowClick}
                setSelected={setSelected}
                setSelectedDeliveryItem={setSelected}
                handleDownloadPdf={onDownloadPdf}
                handleDeleteRow={() => {}}
                handleSortColumn={() => {}}
                operationActions={() => {}}
              />
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default OrderNumberSearchModal;

const createStyles = (colors: any, isDark: boolean) => StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: isDark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.2)",
  } as any,
  modalContainer: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 20,
    elevation: 5,
    maxHeight: "85%",
  } as any,
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.text,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  } as any,
  inputWrapper: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    backgroundColor: colors.surface,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: 4,
    gap: 6,
  } as any,
  searchIcon: {
    paddingLeft: 10,
  },
  input: {
    flex: 1,
    minWidth: 120,
    height: 32,
    paddingHorizontal: 8,
    backgroundColor: "transparent",
    borderWidth: 0,
    color: colors.text,
  } as any,
  searchChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.primaryBg,
    borderRadius: 14,
    paddingLeft: 10,
    paddingRight: 6,
    paddingVertical: 4,
  },
  searchChipText: {
    fontSize: 13,
    color: colors.text,
    marginRight: 4,
  },
  searchChipRemove: {
    padding: 2,
  },
  searchChipX: {
    fontSize: 11,
    color: colors.textSecondary,
  },
  searchButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
  },
  searchText: {
    color: "#fff",
    fontWeight: "bold",
  },
  errorText: {
    color: colors.danger,
    fontSize: 12,
    marginBottom: 8,
  },
  resultsContainer: {
    marginTop: 10,
  },
});
